import type { Container, ObservablePoint } from 'pixi.js';
import type { DisplayObjectProps } from './DisplayObjectProps';
import applyAnchor from './apply/applyAnchor';

export default function applyDisplayObjectProps<T extends Container>(object: T, props: DisplayObjectProps): T {
  if (object.destroyed) return object;

  if (props.position) {
    const { x, y } = props.position;
    object.position.set(x, y);
  }

  if (props.scale !== undefined) {
    if (typeof props.scale === 'number') {
      object.scale.set(props.scale, props.scale);
    } else {
      const { x, y } = props.scale;
      object.scale.set(x, y);
    }
  }

  if (props.scaleXY !== undefined) {
    object.scale.set(props.scaleXY);
  }

  if ('anchor' in object) {
    if (props.anchor !== undefined) {
      applyAnchor(object as T & { anchor: ObservablePoint }, props.anchor);
    }
  }

  if ('tint' in object && props.tint !== undefined) {
    object.tint = props.tint;
  }

  if (props.pivot !== undefined) {
    if (typeof props.pivot === 'number') {
      object.pivot.set(props.pivot, props.pivot);
    } else {
      const { x, y } = props.pivot;
      object.pivot.set(x, y);
    }
  }

  if (props.eventMode !== undefined) object.eventMode = props.eventMode;
  if (props.visible !== undefined) object.visible = props.visible;
  if (props.alpha !== undefined) object.alpha = props.alpha;
  if (props.x !== undefined) object.x = props.x;
  if (props.y !== undefined) object.y = props.y;
  if (props.scaleX !== undefined) object.scaleX = props.scaleX;
  if (props.scaleY !== undefined) object.scaleY = props.scaleY;
  if (props.pivotX !== undefined) object.pivotX = props.pivotX;
  if (props.pivotY !== undefined) object.pivotY = props.pivotY;
  if (props.anchorX !== undefined) object.anchorX = props.anchorX;
  if (props.anchorY !== undefined) object.anchorY = props.anchorY;
  if (props.angle !== undefined) object.angle = props.angle;
  if (props.rotation !== undefined) object.rotation = props.rotation;
  if (props.parent) props.parent.addChild(object as Container);
  if (props.parent === null) object.parent?.removeChild(object);
  if (props.zIndex !== undefined) object.zIndex = props.zIndex;

  if ('width' in object && props.width !== undefined) object.width = props.width;
  if ('height' in object && props.height !== undefined) object.height = props.height;

  if (props.destroyedCallback) object.once('destroyed', props.destroyedCallback);

  if (props.addAbove) {
    if (!object.parent) {
      return object;
    }
    const index = object.parent.children.indexOf(props.addAbove);
    if (index !== -1) object.parent.addChildAt(object, index + 1);
  }

  if (props.addBelow) {
    if (!object.parent) {
      return object;
    }
    const index = object.parent.children.indexOf(props.addBelow);
    if (index !== -1) object.parent.addChildAt(object, index);
  }

  if (props.addAtIndex !== undefined) {
    if (!object.parent) {
      return object;
    }
    object.parent.addChildAt(object, props.addAtIndex);
  }

  if (props.mask !== undefined) {
    object.mask = props.mask;
  }

  if (props.onDestroyed) {
    object.on('destroyed', props.onDestroyed);
  }

  if (props.cursor) {
    object.cursor = props.cursor;
  }

  if (props.label) {
    object.label = props.label;
  }

  return object;
}
